<template>
  <div class="font-sora text-white relative">
    <!-- Uyarı bileşeni -->
    <UiAlert v-model="showAlert" :alert="alertData" :auto-close="true" />

    <!-- Login Modal -->
    <div
      v-if="showLogin"
      class="fixed inset-0 bg-black/60 z-50 flex justify-center items-end md:items-center overflow-y-auto md:py-10"
      @click.self="closeLogin"
    >
      <div class="w-full max-w-md px-4 pb-6 md:pb-0">
        <LoginModal
          :form="loginForm"
          @close="closeLogin"
          @change="changeSignup"
          @alert="handleAlert"
        />
      </div>
    </div>

    <!-- Signup Modal -->
    <div
      v-if="showSignup"
      class="fixed inset-0 bg-black/60 z-50 flex justify-center items-end md:items-center overflow-y-auto md:py-10"
      @click.self="closeSignup"
    >
      <div class="w-full max-w-md px-4 pb-6 md:pb-0">
        <SignupModal
          :form="signupForm"
          @update:form="(val) => Object.assign(signupForm, val)"
          @close="closeSignup"
          @change="changeLogin"
          @alert="handleAlert"
        />
      </div>
    </div>

    <!-- Navbar -->
    <nav
      :class="[
        'w-full z-50 flex justify-between items-center',
        'pl-20 pr-4 sm:pr-6 sm:pl-20 md:pr-8 md:pl-20 lg:pr-12 lg:pl-20 xl:px-15 2xl:pl-40 2xl:pr-20',
        'py-4 md:py-6 border-b border-[#1F1F1F] backdrop-blur-md',
        isFixed
          ? 'fixed top-0 left-0 bg-[#0F0F0F]/20'
          : 'relative bg-gradient-to-br from-[#4b4f53]/60 via-[#454a51]/40 to-[#434851]/60 text-white shadow-md',
      ]"
    >
      <!-- Logo -->
      <div class="flex items-center gap-4">
        <NuxtLink to="/">
          <img
            src="/assets/images/logo.webp"
            alt="MEM22 Logo"
            class="w-12 h-12 md:w-16 md:h-16 xl:w-20 xl:h-20 rounded-full object-cover aspect-square"
          />
        </NuxtLink>
      </div>

      <!-- Sağ Menü -->
      <div class="flex items-center gap-4">
        <!-- Hamburger -->
        <LanguageSwitcher />
        <button
          class="xl:hidden focus:outline-none"
          @click="menuOpen = !menuOpen"
        >
          <svg
            v-if="!menuOpen"
            xmlns="http://www.w3.org/2000/svg"
            class="h-6 w-6 text-white"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
          <svg
            v-else
            xmlns="http://www.w3.org/2000/svg"
            class="h-6 w-6 text-white"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        <!-- Desktop Nav -->
        <div
          class="hidden xl:flex gap-2 lg:gap-3 xl:gap-3 items-center text-sm font-medium"
        >
          <NavLinks @login-click="openLogin" @signup-click="openSignup" />
        </div>
      </div>
    </nav>

    <!-- Mobile Nav -->
    <transition name="fade">
      <div
        v-if="menuOpen"
        class="fixed top-[72px] left-0 w-full z-40 bg-[#0F0F0F] px-6 py-6 border-b border-[#1F1F1F] shadow-xl md:top-[100px] xl:hidden"
      >
        <NavLinks
          :vertical="true"
          @login-click="openLogin"
          @signup-click="openSignup"
        />
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive } from "vue";
import NavLinks from "./NavLinks.vue";
import LoginModal from "./LoginModal.vue";
import SignupModal from "./SignupModal.vue";
import { useAuthModal } from "~/composables/useAuthModal";

// Props
defineProps<{ isFixed?: boolean }>();

// Auth modals
const {
  showLogin,
  showSignup,
  openLogin,
  openSignup,
  closeLogin,
  closeSignup,
} = useAuthModal();

// Form states
const loginForm = reactive({ email: "", password: "" });
const signupForm = reactive({ email: "", password: "" });

// Menü kontrolü
const menuOpen = ref(false);

// Modal geçişleri
const changeSignup = () => {
  showLogin.value = false;
  showSignup.value = true;
};
const changeLogin = () => {
  showLogin.value = true;
  showSignup.value = false;
};

// Alert sistemi
const showAlert = ref(false);
const alertData = ref({
  message: "",
  icon: "",
  type: "info", // success, error, warning, info
});

const handleAlert = (payload: typeof alertData.value) => {
  alertData.value = payload;
  showAlert.value = true;
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Sora:wght@400;600&display=swap");

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
